import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import {
  useAssignProduct,
  useSetAssignedProductReceiptImg,
  useUpdateAssignedProduct,
} from 'app/core/api/product-assignment/api-product-assignment';
import {
  AssignedProduct,
  AssignProductReq,
} from 'app/core/api/product-assignment/api-product-assignment.types';
import { AppSnackbarOpts } from 'app/shared/components/dialog/AppSnackbar';
import { Opt } from 'app/tools/general.types';
import { useQueryParams } from 'app/tools/hooks/misc-hooks';
import { BLANK$, toOptNumber } from 'app/tools/utils';
import { countries } from 'countries-list';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { Observable } from 'rxjs';
import { map, mapTo, switchMap } from 'rxjs/operators';
import { Except } from 'type-fest';

export const countriesList = _(countries)
  .entries()
  .map(([code, country]) => ({ code, ...country }))
  .sortBy(country => country.name)
  .value();

const matchCountryUniversal = (
  input: Opt<string>,
  nameOnly: boolean
): Opt<FullCountry> =>
  input
    ? countriesList.find(c => {
        const codeMatcher = nameOnly ? [] : [c.code.toLowerCase()];
        const matchers = [c.name.toLowerCase(), ...codeMatcher];
        return matchers.includes(input.toLowerCase());
      })
    : undefined;

export const matchCountryAny = (input?: string): Opt<FullCountry> =>
  matchCountryUniversal(input, false);
export const matchCountryName = (input?: string): Opt<FullCountry> =>
  matchCountryUniversal(input, true);

export type FullCountry = typeof countriesList[number];

export type AssignedProductDetailsFormValues = {
  purchaseDate: Date;
  purchasePlace: string;
  purchaseCountry: string;
  expiryTerm?: string;
  purchasePriceAmount?: string;
  purchasePriceCurrency: string;
  durationUnits: 'months' | 'years';
  serialNumber?: string;
  isFromSale: string;
};

const toDateParamArrayOrVoid = (input?: string) =>
  (input ? [input] : []) as Parameters<DateConstructor>;

export const mapDefaults = (
  assignedProduct: Opt<AssignedProduct>,
  defaultCountry?: string
): Opt<Partial<AssignedProductDetailsFormValues>> => ({
  purchaseDate: new Date(
    ...toDateParamArrayOrVoid(assignedProduct?.purchaseDate)
  ),
  purchasePlace: assignedProduct?.purchasePlace,
  purchaseCountry: assignedProduct?.purchaseCountry || defaultCountry,
  expiryTerm: assignedProduct?.expiryTerm?.toFixed(),
  purchasePriceAmount:
    assignedProduct?.purchasePrice?.amount?.toString(),
  purchasePriceCurrency:
    assignedProduct?.purchasePrice?.currency?.toString() || 'GBP',
  durationUnits: 'months',
  serialNumber: assignedProduct?.serialNumber,
});

type SubmitAssignedProductDetailsHookOpts = {
  updateModeLoaded?: boolean | null;
  imgUpload: File | null;
};
type SubmitAssignedProductDetailsHook = (
  formValues: AssignedProductDetailsFormValues,
  purchaseCountryObject: FullCountry
) => Observable<AppSnackbarOpts>;

export const useHandleSubmitAssignedProductDetails = ({
  updateModeLoaded,
  imgUpload,
}: SubmitAssignedProductDetailsHookOpts): SubmitAssignedProductDetailsHook => {
  const history = useHistory();
  const params = useQueryParams();
  const { request$: assignProduct$ } = useAssignProduct();
  const { request$: updateAssignment$ } = useUpdateAssignedProduct();
  const { request$: setAssignedProductImage$ } =
    useSetAssignedProductReceiptImg();

  const productVariantId =
    params.get(UrlQueryKey.PRODUCT_VARIANT_ID) || '';
  const productAssignmentId = params.get(
    UrlQueryKey.PRODUCT_ASSIGNMENT_ID
  );
  const isNewDatabaseProduct = Boolean(
    params.get(UrlQueryKey.IS_NEW_DATABASE_PRODUCT)
  );

  return (
    {
      purchaseDate,
      purchasePlace,
      expiryTerm: expiryTermStr,
      purchasePriceAmount,
      purchasePriceCurrency,
      durationUnits,
      serialNumber,
      isFromSale,
    },
    fullCountryObject
  ) => {
    if (updateModeLoaded === undefined) {
      throw new Error(
        'Defaults not loaded (should never occur, due to disabled button)'
      );
    }

    const expiryTerm = toOptNumber(expiryTermStr);
    const durationMonths = !expiryTerm
      ? null
      : durationUnits === 'years'
      ? 12 * Number(expiryTerm)
      : Number(expiryTerm);

    const purchaseAmount = toOptNumber(purchasePriceAmount)?.toFixed(
      2
    );
    const purchasePrice = purchaseAmount
      ? {
          amount: purchaseAmount,
          currency: purchasePriceCurrency,
        }
      : undefined;
    const isFromSaleCheck = Boolean(JSON.parse(isFromSale));
    const productIdentifier = isNewDatabaseProduct
      ? { upcProductId: productVariantId }
      : { productVariant: productVariantId };

    const msg = `Product ${updateModeLoaded ? 'updated' : 'added'}!`;
    const action = updateModeLoaded
      ? undefined
      : {
          text: 'Add another',
          callback: () =>
            history.push(ConsumerRoutePath.PRODUCT_SEARCH_CATALOG),
        };

    return BLANK$.pipe(
      map(
        (): Except<AssignProductReq, 'productVariant'> => ({
          purchasePlace,
          purchaseDate:
            purchaseDate && purchaseDate.toISOString().slice(0, 10), // just date part
          purchasePrice,
          purchaseCountry: fullCountryObject.code,
          expiryTerm: durationMonths,
          serialNumber,
          isFromSale: isFromSaleCheck,
        })
      ),
      switchMap(partialData =>
        updateModeLoaded
          ? updateAssignment$({
              id: Number(productAssignmentId ?? NaN),
              data: partialData,
            })
          : assignProduct$({ ...productIdentifier, ...partialData })
      ),
      switchMap(({ id }) =>
        setAssignedProductImage$({
          data: { receiptImage: imgUpload ?? undefined },
          id,
        })
      ),
      mapTo({ message: msg, action })
    );
  };
};
